import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { flowRegistry } from './flowRegistry';
import type { OnboardingStepParams } from './types';

/**
 * Hook to read onboarding-specific query parameters from the URL.
 * Returns the onboarding flow name, step index, and resolved selector if valid,
 * otherwise returns undefined.
 *
 * The onboardingFlow should match an exported flow variable name (e.g., "individualTargetRule").
 * The step should be a numeric index (e.g., "0", "1", "2").
 *
 * Expected query params:
 * - onboardingFlow: The exported name of the flow (e.g., "individualTargetRule")
 * - step: A numeric step index (e.g., "0", "1", "2")
 *
 * @example
 * ```tsx
 * // URL: ?onboardingFlow=individualTargetRule&step=0
 * const params = useOnboardingQueryParams();
 * if (params) {
 *   log(params.selector); // "install-flag-in-code"
 *   log(params.stepIndex); // 0
 * }
 * ```
 */
export function useOnboardingQueryParams(): OnboardingStepParams | undefined {
  const [searchParams] = useSearchParams();

  const onboardingFlow = searchParams.get('onboardingFlow');
  const step = searchParams.get('step');

  // Memoize the return value to prevent unnecessary re-renders
  // Only recompute when the actual query parameter values change
  return useMemo(() => {
    // Only proceed if both are present
    if (!onboardingFlow || !step) {
      return undefined;
    }

    // Step must be a numeric index
    const stepIndex = Number(step);
    if (isNaN(stepIndex) || step !== String(stepIndex)) {
      return undefined;
    }

    // Look up the flow by its exported name
    const flow = flowRegistry[onboardingFlow];

    if (!flow || !flow.steps) {
      return undefined;
    }

    // Get the step from the flow
    const stepDef = flow.steps[stepIndex];

    if (!stepDef) {
      return undefined;
    }

    return {
      onboardingFlow,
      stepIndex,
      selector: stepDef.selector,
    };
  }, [onboardingFlow, step]);
}
